import React, {useEffect, useState} from "react";
import {useAuth} from "../../auth";
import {ApiError} from "../../api/client";
import {adminChats} from "../../api/admin/orderChats";
import type {AdminChat, AdminChatFilters, AdminChatPage} from "../../api/admin/orderChats";
import {OrderChat} from "../../components/OrderChat";
import "./AdminChatsPage.css";

const INITIAL_FILTERS: AdminChatFilters = {state: "all", owner: "all", search: ""};
export const ADMIN_CHATS_REFRESH_MS = 10000;
const statusName = (status: string): string => ({new: "Новая", pending: "В работе", appeal: "Спор", success: "Завершена", cancelled: "Отменена"}[status] || status);
const dateLabel = (value: string): string => new Date(value).toLocaleString("ru-RU");

export function AdminChatsPage(): JSX.Element | null {
    const {token} = useAuth();
    return token ? <AdminChats key={token} token={token}/> : null;
}

function AdminChats({token}: {token: string}): JSX.Element {
    const [filters, setFilters] = useState<AdminChatFilters>(INITIAL_FILTERS);
    const [search, setSearch] = useState("");
    const [cursors, setCursors] = useState<string[]>([""]);
    const cursor = cursors[cursors.length - 1];
    const [page, setPage] = useState<AdminChatPage | null>(null);
    const [selected, setSelected] = useState<AdminChat | null>(null);
    const [error, setError] = useState("");
    const [refreshVersion, setRefreshVersion] = useState(0);

    useEffect(() => {
        let disposed = false;
        let reading = false;
        const controller = new AbortController();
        setPage(null);
        setError("");
        const refresh = async (): Promise<void> => {
            if (disposed || reading || document.hidden || !navigator.onLine) return;
            reading = true;
            try {
                const result = await adminChats(token, filters, cursor, controller.signal);
                if (disposed) return;
                setPage(result);
                setError("");
                setSelected(current => !result.enabled ? null : result.items.find(item => item.order_id === current?.order_id) || current);
            } catch (failure) {
                if (disposed) return;
                setPage(null);
                if (failure instanceof ApiError && [401, 403].includes(failure.status)) {
                    setSelected(null);
                    setError("Нет доступа к чатам. Нужна роль администратора.");
                } else {
                    setError("Не удалось загрузить чаты. Проверьте соединение и повторите.");
                }
            } finally { reading = false; }
        };
        const wake = (): void => { void refresh(); };
        wake();
        const timer = window.setInterval(wake, ADMIN_CHATS_REFRESH_MS);
        window.addEventListener("focus", wake);
        window.addEventListener("online", wake);
        document.addEventListener("visibilitychange", wake);
        return () => {
            disposed = true;
            controller.abort();
            window.clearInterval(timer);
            window.removeEventListener("focus", wake);
            window.removeEventListener("online", wake);
            document.removeEventListener("visibilitychange", wake);
        };
    }, [token, filters, cursor, refreshVersion]);

    useEffect(() => {
        const close = (event: KeyboardEvent): void => { if (event.key === "Escape") setSelected(null); };
        window.addEventListener("keydown", close);
        return () => window.removeEventListener("keydown", close);
    }, []);

    const applyFilters = (next: AdminChatFilters): void => {
        setFilters(next);
        setCursors([""]);
        setSelected(null);
    };

    return <div className={`page admin-chats-page ${selected ? "chat-open" : ""}`}>
        <div className="page-header"><div><h2>Чаты по заявкам</h2><p className="admin-chats-description">Переписка клиентов Mini App с поддержкой, включая завершённые заявки.</p></div></div>
        <form className="admin-chats-filters" onSubmit={event => { event.preventDefault(); applyFilters({...filters, search: search.trim()}); }}>
            <label>Заявки<select aria-label="Заявки" value={filters.state} onChange={event => applyFilters({...filters, state: event.target.value as AdminChatFilters["state"]})}>
                <option value="all">Все заявки</option><option value="active">Активные заявки</option><option value="completed">Завершённые заявки</option>
            </select></label>
            <label>Ответственный<select aria-label="Ответственный" value={filters.owner} onChange={event => applyFilters({...filters, owner: event.target.value as AdminChatFilters["owner"]})}>
                <option value="all">Все чаты</option><option value="unassigned">Без доступного ответственного</option>
            </select></label>
            <label className="admin-chats-search">Поиск<input type="search" value={search} maxLength={128} placeholder="ID заявки, внешний ID или трейдер" onChange={event => setSearch(event.target.value)}/></label>
            <button className="btn btn-primary" type="submit">Найти</button>
            <button className="btn btn-ghost" type="button" onClick={() => { setCursors([""]); setRefreshVersion(value => value + 1); }}>Обновить</button>
        </form>
        {error && <p role="alert" className="danger-text">{error}</p>}
        {!page && !error && <p role="status">Загружаем чаты…</p>}
        {page && !page.enabled && <p role="status">Чат отключён в настройках сервера.</p>}
        <div className={`admin-chats-workspace ${selected ? "has-selection" : ""}`}>
            <section className="admin-chats-list" aria-label="Список чатов">
                {page?.enabled && page.items.length === 0 && <p role="status">Чатов по выбранным условиям пока нет.</p>}
                {page?.items.map(item => <button type="button" key={item.order_id} className={`admin-chat-card ${selected?.order_id === item.order_id ? "selected" : ""}`} aria-pressed={selected?.order_id === item.order_id} onClick={() => setSelected(item)}>
                    <span className="admin-chat-card-heading"><strong>{item.remote_id || item.order_id}</strong><span>{statusName(item.order_status)}</span></span>
                    <span className="admin-chat-id">ID: {item.order_id}</span>
                    <span>{item.workflow_kind === "exchange_buy" ? "Покупка USDT" : "Продажа USDT"} · {item.trader_name || "Трейдер не назначен"}</span>
                    {!item.owner_available && <span className="order-chat-owner-warning">{item.trader_id ? "Ответственный недоступен" : "Без ответственного"}</span>}
                    <time dateTime={item.last_message_at}>{dateLabel(item.last_message_at)}</time>
                </button>)}
                {page?.enabled && <div className="admin-chats-pagination">
                    <button type="button" className="btn btn-ghost" disabled={cursors.length === 1} onClick={() => setCursors(value => value.slice(0, -1))}>Назад</button>
                    <span>Страница {cursors.length}</span>
                    <button type="button" className="btn btn-ghost" disabled={!page.next_cursor} onClick={() => setCursors(value => [...value, page.next_cursor])}>Далее</button>
                </div>}
            </section>
            {selected ? <section className="admin-chat-view" aria-label="Просмотр чата">
                <header className="admin-chat-view-header"><div><strong>{selected.remote_id || selected.order_id}</strong><span>{statusName(selected.order_status)} · {selected.trader_name || "Без ответственного"}</span></div><button type="button" className="btn btn-ghost" aria-label="Закрыть чат" onClick={() => setSelected(null)}>Закрыть</button></header>
                <OrderChat token={token} orderID={selected.order_id} mode="admin"/>
            </section> : <div className="admin-chat-placeholder">Выберите чат, чтобы посмотреть сообщения и вложения.</div>}
        </div>
    </div>;
}
